<template>
    <div class="card" v-show="loaded">

        <div class="card-header border-0">
            <div class="row">
                <div class="col-4">
                    <h3 class="mb-0 text-uppercase">{{ $t('common.registrations') }}</h3>
                </div>
                <div class="col-8 text-right" :class="{'pr-0': canDeleteAll}">

                    <base-button type="secondary" size="sm" @click="clearFilters" v-if="hasFilters">
                        {{ $t('common.clear-filters') }}
                    </base-button>

                    <base-button type="secondary" size="sm" @click="createValidation()">
                        <span class="btn-inner--icon"><i class="tim-icons icon-e-add"></i></span>
                        <span class="btn-inner--text">{{ $t('registrations.new-validation') }}</span>
                    </base-button>

                    <base-button type="primary" size="sm" @click="create()">
                        <span class="btn-inner--icon"><i class="tim-icons icon-e-add"></i></span>
                        <span class="btn-inner--text">{{ $t('registrations.new') }}</span>
                    </base-button>

                    <actions-dropdown :actions="mainActions()" v-if="canDeleteAll"></actions-dropdown>
                </div>
            </div>
        </div>

        <div class="pt-2">
            
            <table-control :paging="ctrl.paging" :search="true" v-model="search" ref="search"></table-control>

            <el-table class="table-responsive table-flush"
                    header-row-class-name="thead-light"
                    :data="ctrl.rows"
                    @sort-change="sortChange"
                    @filter-change="filterChange"
                    ref="table">

                <el-table-column :label="$t('common.creation')"
                    sortable="custom" prop="created_at"
                >
                    <template v-slot="{row}">
                        <small>
                            {{ creationDate(row) }}
                        </small>
                    </template>
                </el-table-column>

                <el-table-column :label="$t('common.cohort')"
                    sortable="custom" prop="cohort.name"
                >
                    <template v-slot="{row}">
                        <h4 class="mb-0">
                            {{ row.cohort.name }}
                        </h4>
                    </template>
                </el-table-column>

                <el-table-column :label="$t('common.degree')"
                    sortable="custom" prop="degree.name"
                >
                    <template v-slot="{row}">
                        <h4 class="mb-0">
                            {{ row.degree.name }}
                        </h4>
                        <badge type="warning" v-if="row.degree.status=='validation'">
                            {{ $t('status.validation') }}
                        </badge>
                    </template>
                </el-table-column>

                <el-table-column :label="$t('common.status')"
                    column-key="status"
                    :filters="filtering.filteringValues(allStatusOptions)"
                    :filtered-value="filters.status"
                    filter-placement="bottom-end"
                >
                    <template v-slot="{row}">
                        <badge :type="statusColor(row.status)">{{ statusLabel(row.status) }}</badge>
                        <badge type="warning" v-if="!row.track">
                            {{ $t('registrations.track-disabled') }}
                        </badge>
                    </template>
                </el-table-column>

                <el-table-column :label="$t('common.owner')"
                    column-key="owner"
                    :filters="filtering.filteringValues(owners)"
                    :filtered-value="filters.owner"
                    filter-placement="bottom-end"
                >
                    <template v-slot="{row}">
                        {{ owners[row.cohort.owner_id] }}
                    </template>
                </el-table-column>

                <el-table-column>
                    <template v-slot="{row}">
                        <a href="#" @click.prevent="openTesters(row)">
                            <small>
                                {{ $t('common.testers-n', { n: row.testers_count })  }}
                            </small>
                        </a>
                        <br>
                        <a href="#" @click.prevent="openSessions(row)" v-if="row.status != 'draft'">
                            <small>
                                {{ $t('common.sessions-n', { n: row.sessions_count }) }}
                            </small>
                        </a>
                        <small v-if="row.status == 'closed' || row.status == 'archived'">
                            | <a href="#" @click.prevent="openGrades(row)">
                                {{ $t('common.grades-n', { n: row.grades_count }) }}
                            </a>
                        </small>
                    </template>
                </el-table-column>

                <el-table-column align="right" width="120">
                    <template v-slot="{row}">
                        <div class="table-actions">

                            <a href="#" class="table-action text-info" @click.prevent="close(row)" v-if="canClose(row)">
                                <i class="tim-icons icon-check-single"></i>
                            </a>

                            <a href="#" class="table-action text-primary" @click.prevent="update(row)" v-if="canEdit(row)">
                                <i class="tim-icons icon-pencil"></i>
                            </a>
                        </div>
                    </template>
                </el-table-column>
                                
                <el-table-column align="right" width="50" class-name="actions">
                    <template v-slot="{row}">
                        <div class="table-actions">
                            <actions-dropdown :actions="actions(row)"></actions-dropdown>
                        </div>
                    </template>
                </el-table-column>

            </el-table>

            <table-pagination :paging="ctrl.paging"></table-pagination>
        </div>

        <portal to="modals">

            <form-modal v-model="ctrl.modals.edit"
                :title="$t('common.registration')"
                :loading="ctrl.form.loading"
            >
                <registration-form v-model="ctrl.form.data"
                    :errors="ctrl.form.errors"
                    :modal="ctrl.modals.edit"
                    :cohorts="cohorts"
                    :degrees="activeDegrees"
                    :owners="owners"
                    :status-options="statusOptions"
                    :duplicating="ctrl.form.duplicating"
                    @next="next = true"
                >
                </registration-form>
            </form-modal>

            <form-modal v-model="ctrl.modals.validation"
                :title="$t('registrations.validation')"
                :loading="ctrl.form.loading"
            >
                <registration-form v-model="ctrl.form.data"
                    :errors="ctrl.form.errors"
                    :modal="ctrl.modals.validation"
                    :cohorts="cohorts"
                    :degrees="validationDegrees"
                    :owners="owners"
                    :status-options="statusOptions"
                    :duplicating="ctrl.form.duplicating"
                    :validation="true"
                >
                </registration-form>
            </form-modal>

            <confirm-modal v-model="ctrl.modals.confirmDelete">
                <p>{{ $t('registrations.delete-confirm') }}</p>
            </confirm-modal>

            <confirm-modal v-model="ctrl.modals.confirmClose">
                <p>{{ $t('sessions.finalize-confirm') }}</p>
            </confirm-modal>

            <confirm-modal v-model="ctrl.modals.confirmUnclose">
                <p>{{ $t('sessions.unfinalize-confirm') }}</p>
            </confirm-modal>

            <confirm-modal v-model="ctrl.modals.confirmArchive">
                <p>{{ $t('status.archive-confirm') }}</p>
            </confirm-modal>

            <confirm-modal v-model="ctrl.modals.confirmRestore">
                <p>{{ $t('status.restore-confirm') }}</p>
            </confirm-modal>

        </portal>

    </div>
</template>

<script>
    import CrudController from '@trax/classes/CrudController'
    import CrudFiltering from '../../../classes/CrudFiltering'
    import RegistrationForm from './RegistrationForm'
    import moment from 'moment'

    export default {

        components: {
            RegistrationForm
        },

        data() {
            let filtering = new CrudFiltering('crud_registrations')
            return {
                loaded: false,
                filtering,
                ctrl: new CrudController({
                    endpoint: '/socrates/api/front/registrations',
                    mapData: this.mapData,
                    unmapData: this.unmapData,
                    modals: ['validation', 'confirmClose', 'confirmUnclose', 'confirmArchive', 'confirmRestore'],
                    paging: {
                        perPage: 10,
                        perPageOptions: [5, 10, 25, 50],
                        total: 0
                    },
                    query: {
                        sort: ['-created_at'],
                        relations: ['cohort', 'degree'],
                        include: ['status_options', 'cohorts', 'active_degrees', 'validation_degrees', 'owners'],
                        accessors: ['testers_count', 'sessions_count', 'grades_count', 'sessions_status']
                    },
                    callbacks: {
                        created: this.thenOpen,
                        updated: this.thenOpen,
                        duplicated: this.duplicated,
                        listed: this.listed
                    }
                }),
                cohorts: {},
                activeDegrees: {},
                validationDegrees: {},
                owners: {},
                allStatusOptions: {},
                statusOptions: {},
                moment: moment,
                editValidation: false,
                next: false,
                search: '',
                filters: {},
            }
        },

        computed: {

            canDeleteAll() {
                return this.$auth.hasPermission('admin.delete-personal-data')
            },

            empty() {
                return this.ctrl.rows.length == 0 && !this.search
            },

            hasFilters() {
                return Object.keys(this.filters).length > 0 || this.ctrl.query.sort[0] != '-created_at'
            }
        },

        created() {
            this.filtering.init(this)

            this.ctrl.init(this, 'ctrl').then(resp => {
                this.loaded = true
                this.cohorts = Array.isArray(resp.data.included.cohorts) ? {} : resp.data.included.cohorts
                this.activeDegrees = Array.isArray(resp.data.included.active_degrees) ? {} : resp.data.included.active_degrees
                this.validationDegrees = Array.isArray(resp.data.included.validation_degrees) ? {} : resp.data.included.validation_degrees
                this.owners = Array.isArray(resp.data.included.owners) ? {} : resp.data.included.owners
                this.allStatusOptions = resp.data.included.status_options
                this.statusOptions = {...this.allStatusOptions}
                delete this.statusOptions.closed
                delete this.statusOptions.archived
            })
        },

        mounted() {
            if (this.filters.simpleSearch) {
                this.$refs.search.setFilter(this.filters.simpleSearch)
            }
        },

        watch: {
            search(val) {
                if (val === '') {
                    delete this.filters.simpleSearch
                } else {
                    this.filters.simpleSearch = val
                }
                this.filters = {...this.filters}    // Clone to force reactivity
                this.reload()
            }
        },

        methods: {

            sortChange(data) {
                this.filtering.sortChange(data)
            },

            filterChange(data) {
                this.filtering.filterChange(data)
            },

            clearFilters() {
                this.ctrl.query.sort = ['-created_at']
                this.filtering.clearFilters()
            },

            listed() {
                this.filtering.saveUiConfig()
            },

            reload() {
                let filters = {$and: []}
                
                // Owner.
                if (this.filters.owner) {
                    let inOwner = {ownerIdIn: this.filters.owner.map(id => parseInt(id)).filter(val => val) }
                    filters.$and.push(inOwner)
                }

                // Status.
                if (this.filters.status) {
                    let inStatus = {status: {$in: this.filters.status }}
                    filters.$and.push(inStatus)
                }

                // Search.
                if (this.filters.simpleSearch) {
                    filters.$and.push({ simpleSearch: this.filters.simpleSearch})
                }

                if (filters.$and.length == 0) {
                    filters = {}
                }
                this.ctrl.filter(filters)
            },

            canEdit(row) {
                return row.status == 'draft' || row.status == 'active'
            },

            canClose(row) {
                return row.status == 'active' && row.sessions_status == 'completed'
            },

            canDelete(row) {
                return this.$auth.hasPermission('admin.delete-personal-data')
            },

            mainActions() {
                return [{
                    label: this.$t('registrations.outdated'),
                    handler: this.deleteOutdated,
                }]
            },

            deleteOutdated() {
                this.$router.push({ name: 'clear-registrations'})
            },

            actions(row) {
                let actions = [
                    {
                        label: this.$t('common.duplicate'),
                        handler: this.duplicate,
                        data: row
                    }
                ]
                /*
                if (this.canClose(row)) {
                    actions.push({
                        label: this.$t('sessions.finalize'),
                        handler: this.close,
                        data: row
                    })
                }
                */
                if (row.status == 'closed' && this.$auth.user.admin) {
                    actions.push({
                        label: this.$t('sessions.unfinalize'),
                        handler: this.uncloseRegistration,
                        data: row
                    })
                }
                if (row.status == 'closed') {
                    actions.push({
                        label: this.$t('status.archive'),
                        handler: this.archive,
                        data: row
                    })
                }
                if (row.status == 'archived' && this.$auth.user.admin) {
                    actions.push({
                        label: this.$t('common.restore-archive'),
                        handler: this.restoreRegistration,
                        data: row
                    })
                }
                if (this.canDelete(row)) {
                    actions.push({
                        label: this.$t('registrations.delete'),
                        handler: this.deleteRegistration,
                        data: row
                    })
                }
                return actions
            },

            duplicate(row) {
                this.ctrl.duplicate(row.id)
            },

            duplicated(resp) {
                this.$loader.hide()
            },

            deleteRegistration(row) {
                this.ctrl.delete(row.id)
            },

            close(row) {
                this.ctrl.modals.open('confirmClose').then(confirmResponse => {
                    if (confirmResponse.confirmed) {
                        row.status = 'closed'
                        this.ctrl.client.update(this.unmapData(row)).then(resp => {
                            this.ctrl.list()
                        })
                    }
                })
            },

            uncloseRegistration(row) {
                this.ctrl.modals.open('confirmUnclose').then(confirmResponse => {
                    if (confirmResponse.confirmed) {
                        row.status = 'active'
                        this.ctrl.client.update(this.unmapData(row)).then(resp => {
                            this.ctrl.list()
                        })
                    }
                })
            },

            archive(row) {
                this.ctrl.modals.open('confirmArchive').then(confirmResponse => {
                    if (confirmResponse.confirmed) {
                        row.status = 'archived'
                        this.ctrl.client.update(this.unmapData(row)).then(resp => {
                            this.ctrl.list()
                        })
                    }
                })
            },

            restoreRegistration(row) {
                this.ctrl.modals.open('confirmRestore').then(confirmResponse => {
                    if (confirmResponse.confirmed) {
                        row.status = 'closed'
                        this.ctrl.client.update(this.unmapData(row)).then(resp => {
                            this.ctrl.list()
                        })
                    }
                })
            },

            create() {
                this.editValidation = false
                this.ctrl.create()
            },

            createValidation() {
                this.editValidation = true
                this.ctrl.create('validation')
            },

            update(row) {
                if (row.degree.status == 'validation') {
                    this.editValidation = true
                    this.ctrl.update(row.id, 'validation')
                } else {
                    this.editValidation = false
                    this.ctrl.update(row.id)
                }
            },

            creationDate(registration) {
                return this.moment(registration.created_at).format('YYYY/MM/DD')
            },
            
            statusColor(status) {
                return {
                    draft: 'primary',
                    active: 'success',
                    closed: 'secondary',
                    archived: 'secondary',
                }[status]
            },

            statusLabel(status) {
                const key = {
                    draft: 'inactive',
                    active: 'active',
                    archived: 'archived',
                    closed: 'closed',
                }[status]
                return this.$t('status.'+key)
            },

            mapData(registration) {
                return {
                    id: registration ? registration.id : null,
                    status: registration ? registration.status : null,
                    cohort_id: registration ? registration.cohort_id : null,
                    degree_id: registration ? registration.degree_id : null,
                    cohort: registration ? registration.cohort : null,
                    degree: registration ? registration.degree : null,
                    track: registration ? (registration.track == 1) : !this.editValidation,
                    created_at: registration ? registration.created_at : null,
                    testers_count: registration ? registration.testers_count : 0,
                    sessions_count: registration ? registration.sessions_count : 0,
                    grades_count: registration ? registration.grades_count : 0,
                    sessions_status: registration ? registration.sessions_status : 0,
                }
            },

            unmapData(data) {
                let res = {...data}
                delete res.cohort
                delete res.degree
                delete res.created_at
                delete res.testers_count
                delete res.sessions_count
                delete res.grades_count
                delete res.sessions_status
                return res
            },

            openTesters(row) {
                this.$router.push({ name: 'admin-registration', params: { id: row.id } })
            },

            openSessions(row) {
                this.$router.push({ name: 'dashboard-registration-sessions', params: { registrationId: row.id } })
            },

            openGrades(row) {
                this.$router.push({ name: 'dashboard-registration-grades', params: { registrationId: row.id } })
            },

            thenOpen(resp) {
                if (this.next) {
                    this.$router.push({ name: 'admin-registration', params: { id: resp.data.data.id } })
                }
            },
        }
    }
</script>

<style scoped>

    .card >>> th,
    .card >>> td {
        padding-right: 0;
    }
    .card >>> th.actions,
    .card >>> td.actions {
        padding-left: 0;
        padding-right: 1.5rem;
    }

</style>
